import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useMap } from "@vis.gl/react-google-maps";
import {
  TerraDraw,
  TerraDrawPointMode,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import uuidv4 from "uuid/v4";
import {
  mapSetMarkers,
  mapSetCircles,
  mapSetPolygons,
  mapClearMarker,
  mapClearCircle,
  mapClearPolygon,
  mapDrawingMode
} from "../../reducers/map";

const MODE_MAP = { marker: "point", circle: "circle", polygon: "polygon", rectangle: "rectangle" };

// While TerraDraw is *started* its adapter is bound to the map and intercepts
// pointer events for drawing. We don't want that when the user isn't drawing, so
// fully stop() when no drawing mode is active and only start() when one is entered
// (stop() also unbinds the adapter cleanly; "static" mode would leave it bound).
function setActiveMode(draw, drawingMode) {
  const mode = MODE_MAP[drawingMode];
  if (mode) {
    if (!draw.enabled) draw.start();
    draw.setMode(mode);
  } else if (draw.enabled) {
    draw.stop();
  }
}

function metresBetween(a, b) {
  const R = 6371000,
    toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat),
    dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// GeoJSON polygon/circle rings are closed (last point == first); strip it before use.
function openRing(coords) {
  const ring = coords.map(([lng, lat]) => ({ lat, lng }));
  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) ring.pop();
  }
  return ring;
}

function DrawingMap(props) {
  const map = useMap();
  const drawRef = useRef(null);

  useEffect(() => {
    if (!map || drawRef.current) return;
    let cancelled = false;
    let idleListener = null;

    const init = () => {
      if (cancelled || drawRef.current) return;
      const draw = new TerraDraw({
        // isolatedData: render into terra-draw's OWN google.maps.Data layer (torn down
        // on stop) instead of the shared `map.data`, so terra-draw never mutates the
        // app's shared data layer.
        adapter: new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps, isolatedData: true }),
        modes: [
          new TerraDrawPointMode(),
          new TerraDrawCircleMode(),
          new TerraDrawPolygonMode(),
          new TerraDrawRectangleMode()
        ]
      });
      drawRef.current = draw;

      draw.on("finish", (id, context) => {
        const snapshot = draw.getSnapshot();
        const feature = snapshot.find((f) => f.id === id);
        if (!feature) return;
        const g = feature.geometry;
        const mode = context.mode;

        // Defer ALL side effects out of terra-draw's finish callback. Running
        // draw.clear() (and the dispatch that triggers draw.stop()) synchronously
        // from inside terra-draw's own pointer-event processing corrupts the map's
        // drag handling — afterwards native Polygons/Markers cannot be dragged.
        setTimeout(() => {
          const d = drawRef.current;
          if (d && d.enabled) d.clear();

          if (mode === "point") {
            const [lng, lat] = g.coordinates;
            props.DRAWING_MODE(null);
            props.SET_MARKERS([
              {
                id: uuidv4(),
                position: { lat, lng },
                info: { name: lat + "," + lng, description: "", logisticsPoint: "" },
                isGroup: false,
                editable: true
              }
            ]);
          } else if (mode === "circle") {
            const ring = openRing(g.coordinates[0]);
            const center = ring.reduce(
              (acc, p) => ({ lat: acc.lat + p.lat / ring.length, lng: acc.lng + p.lng / ring.length }),
              { lat: 0, lng: 0 }
            );
            // terra-draw stores the true radius in km on the feature; use it for an exact value.
            const radiusKm = feature.properties && feature.properties.radiusKilometers;
            const radius = typeof radiusKm === "number" ? radiusKm * 1000 : metresBetween(center, ring[0]);
            props.DRAWING_MODE(null);
            props.CLEAR_POLYGON(null);
            props.SET_CIRCLES([
              {
                id: uuidv4(),
                center,
                radius,
                info: { name: center.lat + "," + center.lng, description: "", logisticsPoint: "" },
                isGroup: true,
                editable: true
              }
            ]);
          } else {
            const path = openRing(g.coordinates[0]);
            props.DRAWING_MODE(null);
            props.CLEAR_CIRCLE(null);
            props.SET_POLYGON([
              {
                id: uuidv4(),
                path,
                info: { name: "", description: "", logisticsPoint: "" },
                isGroup: true,
                editable: true
              }
            ]);
          }
        }, 0);
      });

      // Apply whatever mode is active now (idle init may fire after drawingMode
      // changed). Only starts the adapter if a drawing mode is actually set.
      setActiveMode(draw, props.drawingMode);
    };

    // terra-draw's Google Maps adapter (its OverlayView) can only attach once the map has
    // rendered. Mounting eagerly (see BaseMap) can run before that, so defer init until the
    // map's first `idle`. If the map is already settled, init now.
    if (map.getProjection()) {
      init();
    } else {
      idleListener = window.google.maps.event.addListenerOnce(map, "idle", init);
    }

    return () => {
      cancelled = true;
      if (idleListener) {
        window.google.maps.event.removeListener(idleListener);
        idleListener = null;
      }
      if (drawRef.current) {
        try {
          drawRef.current.stop();
        } catch (e) {}
        drawRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;
    try {
      setActiveMode(draw, props.drawingMode);
    } catch (e) {}
  }, [props.drawingMode]);

  return null;
}

const mapStateToProps = (state) => ({ drawingMode: state.map.drawingMode });
const mapActionsToProps = {
  SET_MARKERS: mapSetMarkers,
  SET_CIRCLES: mapSetCircles,
  SET_POLYGON: mapSetPolygons,
  DRAWING_MODE: mapDrawingMode,
  CLEAR_POLYGON: mapClearPolygon,
  CLEAR_CIRCLE: mapClearCircle,
  CLEAR_MARKER: mapClearMarker
};

export default connect(mapStateToProps, mapActionsToProps)(DrawingMap);
